import { useState } from "react";
import { MembershipManager } from "@/components/shared/membership";
import { agentMembershipService } from "@/lib/services";
import type { RoleOption } from "@/types";
import type { AgentShareImpactResponse } from "@/types/sharing";

// ============================================================================
// Agent Access Tab — role-based membership over the shared MembershipManager.
//
// Agent-specific extra: an advisory warning when a newly-added person/team
// can't use some of the agent's extensions. Everything else (search box,
// member rows, role dropdowns, CRUD) is the shared membership surface.
// ============================================================================

export interface AgentAccessTabProps {
  agentUuid: string;
}

const ROLE_OPTIONS: RoleOption[] = [
  { value: "admin", label: "Admin", description: "Edit + manage access" },
  { value: "editor", label: "Editor", description: "Edit + use" },
  { value: "viewer", label: "Viewer", description: "View + use" },
];

function ExtensionWarning({ impact }: Readonly<{ impact: AgentShareImpactResponse }>) {
  const exts = impact.inaccessibleExtensions;
  return (
    <div className="flex items-start gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-300">
      <div className="flex-1 min-w-0">
        <span className="font-medium">{impact.targetName}</span> can&apos;t use{" "}
        {exts.map((e, i) => (
          <span key={e.uuid}>
            {i > 0 && ", "}
            <span className="font-medium">{e.name}</span>
            {e.reason === "disabled" ? " (disabled)" : ""}
          </span>
        ))}
        {exts.length === 1 ? " — this capability" : " — these capabilities"} won&apos;t work for them.
      </div>
    </div>
  );
}

export function AgentAccessTab({ agentUuid }: Readonly<AgentAccessTabProps>) {
  const [impact, setImpact] = useState<AgentShareImpactResponse | null>(null);

  const checkImpact = async (target: { userUuid?: string; teamUuid?: string }) => {
    try {
      const response = await agentMembershipService.getMemberImpact(agentUuid, target);
      if (response.data.inaccessibleExtensions.length > 0) setImpact(response.data);
    } catch (err) {
      console.error("Failed to check access impact:", err);
    }
  };

  return (
    <MembershipManager
      service={agentMembershipService}
      entityUuid={agentUuid}
      roleOptions={ROLE_OPTIONS}
      description="Admins can edit the agent and manage access. Editors can edit and use it. Viewers can view and use it."
      loadErrorLabel="Failed to load agent access"
      banner={impact && <ExtensionWarning impact={impact} />}
      onAfterAddUser={(userUuid) => void checkImpact({ userUuid })}
      onAfterAddTeam={(teamUuid) => void checkImpact({ teamUuid })}
    />
  );
}
